import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import {
  SectionAdminBar,
  useBlockConfig,
  ArrayDeleteButton,
  AddItemButton,
  AdminCTAButton,
} from "./shared";

interface SpaceGridBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function SpaceGridBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: SpaceGridBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const eyebrow = get("eyebrow", "");
  const heading = get("heading", "");
  const subheading = get("subheading", "");
  const spaces = get("spaces", []) as {
    id: string;
    title: string;
    subtitle: string;
    price: string;
    term: string;
    details: string[];
    image: string;
    imagePosition?: string;
    ctaLabel?: string;
  }[];
  const bottomHelperText = get("bottomHelperText", "");

  const setSpace = (idx: number, key: string, val: unknown) => {
    const newSpaces = spaces.map((s, i) => (i === idx ? { ...s, [key]: val } : s));
    setConfig("spaces", newSpaces);
  };

  const deleteSpace = (idx: number) => {
    setConfig("spaces", spaces.filter((_, i) => i !== idx));
  };

  const addSpace = () => {
    setConfig("spaces", [
      ...spaces,
      {
        id: `space-new-${Date.now()}`,
        title: "New Space",
        subtitle: "NEW",
        price: "$0/mo",
        term: "Flexible",
        details: ["Detail 1", "Detail 2"],
        image: "",
        imagePosition: "50% 50%",
      },
    ]);
  };

  const addDetail = (spaceIdx: number) => {
    const newSpaces = spaces.map((s, i) =>
      i === spaceIdx ? { ...s, details: [...s.details, "New detail"] } : s
    );
    setConfig("spaces", newSpaces);
  };

  const deleteDetail = (spaceIdx: number, detailIdx: number) => {
    const newSpaces = spaces.map((s, i) =>
      i === spaceIdx ? { ...s, details: s.details.filter((_, di) => di !== detailIdx) } : s
    );
    setConfig("spaces", newSpaces);
  };

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="space-grid"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-16 px-6 rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff9fb" }}>
        <div className="text-center mb-10">
          {eyebrow && (
            <AdminEditField value={eyebrow} onSave={(v) => setConfig("eyebrow", v)} inputClassName="text-center text-sm">
              <span className="text-green-500 text-sm font-bold uppercase tracking-widest">{eyebrow}</span>
            </AdminEditField>
          )}
          {heading && (
            <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
              <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {heading}
              </h2>
            </AdminEditField>
          )}
          {subheading && (
            <AdminEditField value={subheading} onSave={(v) => setConfig("subheading", v)} inputClassName="text-center">
              <p className={`text-base max-w-xl mx-auto ${subColor}`}>{subheading}</p>
            </AdminEditField>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {spaces.map((space, idx) => (
            <div
              key={space.id}
              className={`rounded-2xl overflow-hidden border flex flex-col relative group/space ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100"
              }`}
            >
              {isAdmin && onUpdate && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteSpace(idx); }}
                  className="absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover/space:opacity-100"
                  title="Delete space"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}

              <div className="w-full h-48 overflow-hidden relative">
                {isAdmin && onUpdate ? (
                  <AdminImageUpload
                    src={space.image || ""}
                    alt={space.title}
                    onReplace={(url) => setSpace(idx, "image", url)}
                    className="w-full h-full"
                    imgClassName="w-full h-full object-cover"
                    isDark={isDark}
                    objectPosition={space.imagePosition ?? "50% 50%"}
                    onPositionChange={(pos) => setSpace(idx, "imagePosition", pos)}
                  />
                ) : (
                  <img
                    src={space.image}
                    alt={space.title}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: space.imagePosition ?? "50% 50%" }}
                  />
                )}
              </div>

              <div className="p-5 flex flex-col flex-1">
                <AdminEditField
                  value={space.title}
                  onSave={(v) => setSpace(idx, "title", v)}
                  inputClassName="text-lg font-extrabold"
                >
                  <h3 className={`text-lg font-extrabold mb-1 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                    {space.title}
                  </h3>
                </AdminEditField>

                <AdminEditField
                  value={space.subtitle}
                  onSave={(v) => setSpace(idx, "subtitle", v)}
                  inputClassName="text-xs text-pink-500 font-semibold uppercase tracking-wide"
                >
                  <p className="text-xs text-pink-500 font-semibold uppercase tracking-wide mb-2">{space.subtitle}</p>
                </AdminEditField>

                <AdminEditField
                  value={space.price}
                  onSave={(v) => setSpace(idx, "price", v)}
                  inputClassName="text-xl font-extrabold text-green-600"
                >
                  <div className="text-xl font-extrabold text-green-600 mb-3">{space.price}</div>
                </AdminEditField>

                <ul className={`text-sm space-y-1 mb-4 flex-1 ${subColor}`}>
                  <li className="font-semibold">
                    {isAdmin && onUpdate ? (
                      <AdminEditField
                        value={space.term}
                        onSave={(v) => setSpace(idx, "term", v)}
                        inputClassName="text-sm font-semibold"
                      >
                        Term: {space.term}
                      </AdminEditField>
                    ) : (
                      `Term: ${space.term}`
                    )}
                  </li>
                  {space.details.map((detail, di) => (
                    <li key={di} className="flex items-start gap-1.5 group/detail relative">
                      <i className="ri-checkbox-circle-line text-green-400 mt-0.5 flex-shrink-0"></i>
                      {isAdmin && onUpdate ? (
                        <>
                          <AdminEditField
                            value={detail}
                            onSave={(v) => {
                              const newDetails = space.details.map((d, i) => (i === di ? v : d));
                              setSpace(idx, "details", newDetails);
                            }}
                            inputClassName="text-sm flex-1"
                          >
                            <span>{detail}</span>
                          </AdminEditField>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteDetail(idx, di); }}
                            className="ml-1 w-4 h-4 flex items-center justify-center bg-red-400 hover:bg-red-500 text-white rounded-full cursor-pointer opacity-0 group-hover/detail:opacity-100 transition-opacity flex-shrink-0"
                            title="Remove detail"
                          >
                            <i className="ri-close-line" style={{ fontSize: "8px" }}></i>
                          </button>
                        </>
                      ) : (
                        <span>{detail}</span>
                      )}
                    </li>
                  ))}
                  {isAdmin && onUpdate && (
                    <li>
                      <button
                        onClick={(e) => { e.stopPropagation(); addDetail(idx); }}
                        className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 cursor-pointer mt-1"
                      >
                        <i className="ri-add-line"></i> Add detail
                      </button>
                    </li>
                  )}
                </ul>

                {isAdmin && onUpdate ? (
                  <AdminEditField
                    value={space.ctaLabel ?? "Apply Now"}
                    onSave={(v) => setSpace(idx, "ctaLabel", v)}
                    inputClassName="text-sm font-bold text-center"
                  >
                    <AdminCTAButton variant="outline" className="w-full py-2.5 text-sm">
                      {space.ctaLabel ?? "Apply Now"}
                    </AdminCTAButton>
                  </AdminEditField>
                ) : (
                  <a
                    href="/#application"
                    className="w-full py-2.5 rounded-full text-sm font-bold bg-pink-100 hover:bg-pink-200 text-pink-700 cursor-pointer whitespace-nowrap transition-colors text-center block"
                  >
                    {space.ctaLabel ?? "Apply Now"}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {isAdmin && onUpdate && (
          <AddItemButton onClick={addSpace} label="Add Space" isDark={isDark} className="mt-5 py-5" />
        )}

        {bottomHelperText && (
          <AdminEditField value={bottomHelperText} onSave={(v) => setConfig("bottomHelperText", v)} inputClassName="text-center text-sm mt-8">
            <p className={`text-center text-sm mt-8 ${subColor}`}>{bottomHelperText}</p>
          </AdminEditField>
        )}
      </div>
    </div>
  );
}